import { useState, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLearner } from '../../hooks/useLearner';
import { useCurriculumCharacters } from '../../hooks/useCurriculumCharacters';
import { generateWorksheet } from '../../api/worksheets';
import { CHARACTER_STATE_COLORS } from '../../utils/stateHelpers';
import WorksheetHtmlPreview from './WorksheetHtmlPreview';
import s from './CreateWorksheetFlow.module.css';

const MAX_SELECTED = 5;

function useFilterOptions() {
  const { t } = useTranslation();
  return [
    { key: 'all', label: t('workbook.all') },
    { key: 'to_learn', label: t('common.new') },
    { key: 'practicing', label: t('common.practicing') },
    { key: 'known', label: t('common.known') },
  ];
}

function StatusBadge({ status }) {
  const color = CHARACTER_STATE_COLORS[status] || CHARACTER_STATE_COLORS.to_learn;

  let icon;
  if (status === 'known') {
    icon = (
      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
        <path d="M1 4L3.5 6.5L9 1" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  } else if (status === 'practicing') {
    icon = (
      <svg width="15" height="11" viewBox="0 0 15 11" fill="none">
        <path d="M14.4885 4.07154V8.35718M3.29702 5.85722V8.35718C3.29702 8.92549 3.73918 9.47053 4.52624 9.87238C5.31329 10.2742 6.38077 10.5 7.49383 10.5C8.60689 10.5 9.67437 10.2742 10.4614 9.87238C11.2485 9.47053 11.6906 8.92549 11.6906 8.35718V5.85722M14.0829 4.73018C14.2081 4.67377 14.3143 4.58109 14.3885 4.46362C14.4626 4.34614 14.5014 4.20904 14.5 4.06929C14.4985 3.92954 14.457 3.79329 14.3805 3.6774C14.304 3.56152 14.1959 3.4711 14.0696 3.41735L8.07442 0.628824C7.89216 0.543932 7.69418 0.5 7.49386 0.5C7.29354 0.5 7.09556 0.543932 6.9133 0.628824L0.918857 3.41449C0.794329 3.47018 0.688393 3.56173 0.614005 3.67793C0.539617 3.79412 0.5 3.92994 0.5 4.06876C0.5 4.20759 0.539617 4.34341 0.614005 4.4596C0.688393 4.5758 0.794329 4.66734 0.918857 4.72304L6.9133 7.51442C7.09556 7.59931 7.29354 7.64324 7.49386 7.64324C7.69418 7.64324 7.89216 7.59931 8.07442 7.51442L14.0829 4.73018Z" stroke={color} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  } else {
    icon = (
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M6 1L7.3 4.2L10.8 4.5L8.1 6.7L8.9 10L6 8.2L3.1 10L3.9 6.7L1.2 4.5L4.7 4.2Z" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  return (
    <div
      className={s.statusBadge}
      style={{ border: `1px solid ${color}` }}
    >
      {icon}
    </div>
  );
}

export default function CreateWorksheetFlow({
  initialCharacterIds,
  initialStep = 1,
  onComplete,
  onClose,
  isModal = false,
}) {
  const { t } = useTranslation();
  const { data: activeLearner } = useLearner();
  const queryClient = useQueryClient();
  const learnerId = activeLearner?.id;
  const FILTER_OPTIONS = useFilterOptions();
  const { data: characters = [], isLoading: loading } = useCurriculumCharacters(learnerId);

  const [step, setStep] = useState(1);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [paperSize, setPaperSize] = useState('a4');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Pre-selection effect
  useEffect(() => {
    if (initialCharacterIds?.length && characters.length > 0) {
      const initSet = new Set(initialCharacterIds.slice(0, MAX_SELECTED));
      setSelectedIds(initSet);
      if (initialStep === 2) setStep(2);
    }
  }, [initialCharacterIds, initialStep, characters]);

  // Filtering + Search logic
  const filteredCharacters = useMemo(() => {
    let list = characters;
    if (filter !== 'all') list = list.filter((c) => c.state === filter);
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter((c) =>
        (c.char || '').includes(q) ||
        (c.simplified_char || '').includes(q) ||
        (c.traditional_char || '').includes(q) ||
        (c.pinyin || '').toLowerCase().includes(q) ||
        (c.definition_en || c.meaning || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [characters, filter, searchQuery]);

  // Selected characters for step 2 preview
  const selectedCharacters = useMemo(
    () => characters.filter((c) => selectedIds.has(c.id || c.char)),
    [characters, selectedIds]
  );

  function toggleCharacter(char) {
    const charKey = char.id || char.char;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(charKey)) {
        next.delete(charKey);
      } else if (next.size < MAX_SELECTED) {
        next.add(charKey);
      }
      return next;
    });
  }

  async function handleGenerate() {
    if (selectedIds.size === 0 || submitting) return;
    const lid = activeLearner?.id;
    if (!lid) return;

    const characterIds = characters
      .filter((c) => selectedIds.has(c.id || c.char))
      .map((c) => c.id || c.char);

    setSubmitting(true);
    setError(null);
    try {
      const result = await generateWorksheet({
        learnerId: lid,
        characterIds,
        worksheetType: 'stroke_practice',
        paperSize,
      });
      onComplete(result);
    } catch (err) {
      setError(err.message || 'Failed to create worksheet. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  // Shared header + step counter
  const headerAndSteps = (
    <>
      <div className={s.header}>
        <button type="button" className={s.closeButton} onClick={onClose}>
          {isModal ? t('common.close') : t('common.back')}
        </button>
        <h2 className={s.title}>{t('worksheetCreate.title')}</h2>
      </div>

      <div className={s.stepCounter}>
        {[1, 2].map((n) => (
          <div key={n} className={`${s.stepDot} ${n === step ? s.stepDotActive : ''}`}>
            <span className={s.stepDotLabel}>{n}</span>
          </div>
        ))}
      </div>
    </>
  );

  if (step === 1) {
    return (
      <div className={`${s.flow} ${isModal ? s.flowModal : s.flowPage}`}>
        {headerAndSteps}

        <p className={s.subtitle}>{t('worksheetCreate.selectChars')}</p>

        <div className={s.controlsRow}>
          <input
            type="text"
            className={s.searchInput}
            placeholder={t('worksheetCreate.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <select
            className={s.filterSelect}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            {FILTER_OPTIONS.map((opt) => (
              <option key={opt.key} value={opt.key}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className={s.charGrid}>
          {loading ? (
            <p className={s.loadingText}>{t('worksheetCreate.loadingChars')}</p>
          ) : filteredCharacters.length === 0 ? (
            <p className={s.emptyText}>{t('worksheetCreate.noCharsFound')}</p>
          ) : (
            filteredCharacters.map((char) => {
              const charKey = char.id || char.char;
              const isSelected = selectedIds.has(charKey);
              const isDisabled = !isSelected && selectedIds.size >= MAX_SELECTED;
              return (
                <button
                  key={charKey}
                  type="button"
                  className={`${s.charCard} ${isSelected ? s.charCardSelected : ''}`}
                  onClick={() => toggleCharacter(char)}
                  disabled={isDisabled}
                  aria-pressed={isSelected}
                >
                  {char.state && <StatusBadge status={char.state} />}
                  <span className={s.charCardChar}>{char.char}</span>
                  {char.pinyin && <span className={s.charCardPinyin}>{char.pinyin}</span>}
                  {(char.meaning || char.definition_en) && (
                    <span className={s.charCardMeaning}>{char.meaning || char.definition_en}</span>
                  )}
                </button>
              );
            })
          )}
        </div>

        <button
          type="button"
          className={s.primaryButton}
          onClick={() => setStep(2)}
          disabled={selectedIds.size === 0}
        >
          {t('common.next')}
        </button>
      </div>
    );
  }

  // Step 2
  return (
    <div className={`${s.flow} ${isModal ? s.flowModal : s.flowPage}`}>
      {headerAndSteps}

      <div className={s.step2Layout}>
        <div className={s.previewColumn}>
          <WorksheetHtmlPreview
            characters={selectedCharacters}
            paperSize={paperSize}
          />
        </div>
        <div className={s.optionsColumn}>
          <p className={s.optionLabel}>{t('worksheetCreate.selectFormat')}</p>
          <div className={s.paperToggle}>
            <button
              type="button"
              className={`${s.paperButton} ${paperSize === 'a4' ? s.paperButtonActive : ''}`}
              onClick={() => setPaperSize('a4')}
            >
              {t('worksheetCreate.a4')}
            </button>
            <button
              type="button"
              className={`${s.paperButton} ${paperSize === 'letter' ? s.paperButtonActive : ''}`}
              onClick={() => setPaperSize('letter')}
            >
              {t('worksheetCreate.usLetter')}
            </button>
          </div>

          <div className={s.ttlToast}>
            <p className={s.ttlHeadline}>{t('worksheetCreate.expiryNote')}</p>
            <p className={s.ttlBody}>
              {t('worksheetCreate.expiryExplain')}
            </p>
          </div>
        </div>
      </div>

      {error && <p className={s.errorText} role="alert">{error}</p>}

      <div className={s.step2Buttons}>
        <button type="button" className={s.outlineButton} onClick={() => setStep(1)}>
          {t('worksheetCreate.backToSelection')}
        </button>
        <button
          type="button"
          className={s.primaryButton}
          onClick={handleGenerate}
          disabled={submitting}
        >
          {submitting ? t('worksheetCreate.generating') : t('worksheetCreate.generate')}
        </button>
      </div>
    </div>
  );
}
